import {FilterItem} from '../_models/FilterItem';
import {TipoOperazione} from './TipoOperazione';

export function matchesFilterItems<T extends object>(item: T, filters: FilterItem[] = []): boolean {
  return filters
    .filter((filter) => hasFilterValue(filter))
    .every((filter) => matchesFilterItem(item, filter));
}

export function hasActiveFilterItems(filters: FilterItem[] = []): boolean {
  return filters.some((filter) => hasFilterValue(filter));
}

function matchesFilterItem<T extends object>(item: T, filter: FilterItem): boolean {
  const actual = readFieldValue(item, filter.fieldName);
  if (actual === undefined || actual === null) {
    return false;
  }

  switch (filter.operazione) {
    case TipoOperazione.equals:
      return normalizeText(actual) === normalizeText(filter.value1);
    case TipoOperazione.notEquals:
      return normalizeText(actual) !== normalizeText(filter.value1);
    case TipoOperazione.major:
      return compareValues(actual, filter.value1) > 0;
    case TipoOperazione.minor:
      return compareValues(actual, filter.value1) < 0;
    case TipoOperazione.like:
      return normalizeText(actual).includes(normalizeText(filter.value1));
    case TipoOperazione.startWith:
      return normalizeText(actual).startsWith(normalizeText(filter.value1));
    case TipoOperazione.endWith:
      return normalizeText(actual).endsWith(normalizeText(filter.value1));
    case TipoOperazione.between:
      return compareValues(actual, filter.value1) >= 0 && compareValues(actual, filter.value2) <= 0;
    case TipoOperazione.majorOrEqual:
      return compareValues(actual, filter.value1) >= 0;
    case TipoOperazione.minorOrEqual:
      return compareValues(actual, filter.value1) <= 0;
    default:
      return true;
  }
}

function hasFilterValue(filter: FilterItem): boolean {
  return hasValue(filter?.value1) || (filter?.operazione === TipoOperazione.between && hasValue(filter?.value2));
}

function readFieldValue<T extends object>(item: T, fieldName: string): any {
  if (!fieldName) {
    return undefined;
  }

  if (fieldName === 'CodiceOrdine') {
    const value = item as any;
    return `${value.idDocumento ?? 0}.${value.idRiga ?? 0}.${value.idRigaLavorazione ?? 0}`;
  }

  if (fieldName === 'DataRegistrazione') {
    const value = item as any;
    if (value.dataRegistrazione !== undefined) {
      return value.dataRegistrazione;
    }
    if (value.dataUnixRegistrazione !== undefined) {
      return Number(value.dataUnixRegistrazione) * 1000;
    }
  }

  const direct = (item as any)[fieldName];
  if (direct !== undefined) {
    return direct;
  }

  const camelCaseField = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
  const camelCaseValue = (item as any)[camelCaseField];
  if (camelCaseValue !== undefined) {
    return camelCaseValue;
  }

  const lowerField = fieldName.toLowerCase();
  const key = Object.keys(item).find((candidate) => candidate.toLowerCase() === lowerField);
  return key ? (item as any)[key] : undefined;
}

function compareValues(actual: any, expected: any): number {
  const actualNumber = toComparableNumber(actual);
  const expectedNumber = toComparableNumber(expected);

  if (actualNumber !== undefined && expectedNumber !== undefined) {
    return actualNumber - expectedNumber;
  }

  return normalizeText(actual).localeCompare(normalizeText(expected));
}

function toComparableNumber(value: any): number | undefined {
  if (!hasValue(value)) {
    return undefined;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsedDate = Date.parse(value);
  if (!Number.isNaN(parsedDate) && /[-T:]/.test(value.toString())) {
    return parsedDate;
  }

  const parsedNumber = Number(value.toString().replace(',', '.'));
  return Number.isFinite(parsedNumber) ? parsedNumber : undefined;
}

function normalizeText(value: any): string {
  return hasValue(value) ? value.toString().trim().toLowerCase() : '';
}

function hasValue(value: any): boolean {
  return value !== undefined && value !== null && value.toString().trim() !== '';
}
